import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { initialCadastroAprovadoresLista, useCadastroAprovadoresListaFilters } from "../hooks/CadastroAprovadores_lista/filters";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { FuncionarioAprovador } from "../../domain/models/funcionario_aprovador";
import { FuncionarioAprovacaoDropDown } from "../../domain/models/funcionario_aprovacao";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { CadastroAprovadorForm } from "../schemas/CadastroAprovadores_lista/cadastro_aprovador";

/**
 * LoadAprovadoresFuncState
 */

type LoadAprovadoresFuncState = {
  nomeEmailFuncionario?: string | null;
  centrosCustos?: number[] | null;
};

/**
 * useCadastroAprovadoresListaViewModel
 */

export const useCadastroAprovadoresListaViewModel = () => {
  const {
    compras: {
      getFuncionarioParaAprovacaoDropDownUseCase,
      getAllFuncionariosAprovadoresUseCase,
      createFuncionarioAprovadorUseCase,
      updateFuncionarioAprovadorUseCase,
      deleteFuncionarioAprovadorUseCase,
    }
  } = useDependencyInjection();

  const { loadCentroCustoDropDown } = useDropDownLoader();

  const { pushErrorNotification, pushInfoNotification, setPageSaving } = usePageLoader();

  const debouncedHandler = useCreateDebounceHandler(750);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [funcionariosAprovadores, setFuncionariosAprovadores] = useState<FuncionarioAprovador[]>();

  const [centrosCustoDropdown, setCentrosCustoDropdown] = useState<CentroCustoDropDown[]>();
  const [funcionariosAprovacaoDropdown, setFuncionariosAprovacaoDropdown] = useState<FuncionarioAprovacaoDropDown[]>();
  const [aprovadorModal, setAprovadorModal] = useState(false);
  const [aprovadorEmEdicao, setAprovadorEmEdicao] = useState<FuncionarioAprovador>();
  const [funcionarioAprovadorDeleteModal, setFuncionarioAprovadorDeleteModal] = useState(false);
  const [selectedFuncionarioAprovadorId, setSelectedFuncionarioAprovadorId] = useState<number>();

  const filters = useCadastroAprovadoresListaFilters();

  /**
   * Funcoes de carregamento
   */

  const loadFuncionariosParaAprovacaoDropdown = useCallback(async () => {
    try {
      const results = await getFuncionarioParaAprovacaoDropDownUseCase
        .getFuncionarioParaAprovacaoDropDown();
      setFuncionariosAprovacaoDropdown(results);
    } catch (e: unknown) {
      throw new Error('Falha ao buscar funcionarios para aprovação!');
    }
  }, [
    getFuncionarioParaAprovacaoDropDownUseCase,
  ]);

  const loadAprovadores = useCallback(async (fs?: LoadAprovadoresFuncState) => {
    try {
      const filteredNomeEmailFuncionario = evaluateParamValue(filters.nomeEmailFuncionario, fs?.nomeEmailFuncionario);
      const filteredCentrosCustos = evaluateParamValue(filters.centrosCustos, fs?.centrosCustos);

      const aprovadores = await getAllFuncionariosAprovadoresUseCase
        .getAllFuncionariosAprovadores(
          filteredNomeEmailFuncionario,
          filteredCentrosCustos,
        );

      setFuncionariosAprovadores(aprovadores);
    } catch (e: unknown) {
      throw new Error('Falha ao buscar funcionarios aprovadores!');
    }
  }, [
    getAllFuncionariosAprovadoresUseCase,
    filters,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadCentroCustoDropDown(setCentrosCustoDropdown);
      await loadFuncionariosParaAprovacaoDropdown();
      await loadAprovadores();
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadCentroCustoDropDown(setCentrosCustoDropdown);
      await loadFuncionariosParaAprovacaoDropdown();
      await loadAprovadores();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadAprovadores,
    loadCentroCustoDropDown,
    loadFuncionariosParaAprovacaoDropdown,
    pushErrorNotification,
  ]);

  const handleAprovadorModalOpenState = useCallback((openState: boolean) => {
    setAprovadorEmEdicao(undefined);
    setAprovadorModal(openState);
  }, []);

  const handleAprovadorEdit = useCallback((aprovador: FuncionarioAprovador) => {
    setAprovadorEmEdicao(aprovador);
    setAprovadorModal(true);
  }, []);

  const handleAprovadorSubmit = useCallback(async (form: CadastroAprovadorForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (aprovadorEmEdicao) {
        await updateFuncionarioAprovadorUseCase.updateFuncionarioAprovador(
          aprovadorEmEdicao.idFuncionarioAprovador,
          form.email,
          form.valorMaximo,
        );
      } else {
        await createFuncionarioAprovadorUseCase.createFuncionarioAprovador(
          form.idFuncionario,
          form.email,
          form.valorMaximo,
        );
      }

      await loadAprovadores();
      await loadFuncionariosParaAprovacaoDropdown();

      setAprovadorModal(false);
      setAprovadorEmEdicao(undefined);

      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao salvar aprovador!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    createFuncionarioAprovadorUseCase,
    updateFuncionarioAprovadorUseCase,
    aprovadorEmEdicao,
    loadFuncionariosParaAprovacaoDropdown,
    loadAprovadores,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleFuncionarioAprovadorDeleteOpenState = useCallback((idFuncionarioAprovador: number, openState: boolean) => {
    if (openState) {
      setSelectedFuncionarioAprovadorId(idFuncionarioAprovador);
    } else {
      setSelectedFuncionarioAprovadorId(undefined);
    }
    setFuncionarioAprovadorDeleteModal(openState);
  }, [
  ]);

  const handleFuncionarioAprovadorDeleteSubmit = useCallback(async () => {
    if (selectedFuncionarioAprovadorId === undefined) return;

    try {
      setSaving(true);
      setPageSaving(true);

      await deleteFuncionarioAprovadorUseCase.deleteFuncionarioAprovador(
        selectedFuncionarioAprovadorId,
      );

      await loadAprovadores();
      await loadFuncionariosParaAprovacaoDropdown();

      setFuncionarioAprovadorDeleteModal(false);
      setSelectedFuncionarioAprovadorId(undefined);

      pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao excluir aprovador!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    selectedFuncionarioAprovadorId,
    deleteFuncionarioAprovadorUseCase,
    loadFuncionariosParaAprovacaoDropdown,
    loadAprovadores,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const onNomeEmailFuncionarioChange = useDebouncedCallback(debouncedHandler, async (nomeEmailFuncionario: string | null) => {
    debugger;
    useCadastroAprovadoresListaFilters.setState({
      nomeEmailFuncionario: nomeEmailFuncionario?.trim() || null,
    });
    return async () => {
      try {
        setLoading(true);
        await loadAprovadores({ nomeEmailFuncionario: nomeEmailFuncionario?.trim() || null });
        await loadFuncionariosParaAprovacaoDropdown();
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadAprovadores,
    loadFuncionariosParaAprovacaoDropdown,
  ]);

  const handleCentroCustoChange = useDebouncedCallback(debouncedHandler, async (centrosCustos: number[] | null) => {
    useCadastroAprovadoresListaFilters.setState({ centrosCustos });
    return async () => {
      try {
        setLoading(true);
        await loadAprovadores({ centrosCustos });
        await loadFuncionariosParaAprovacaoDropdown();
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadAprovadores,
    loadFuncionariosParaAprovacaoDropdown,
  ]);

  const handleClearFilters = useCallback(() => {
    useCadastroAprovadoresListaFilters.setState(initialCadastroAprovadoresLista);
  }, [
  ]);


  return {
    loading,
    saving,
    errorMessage,
    funcionarioAprovadores: funcionariosAprovadores ?? [],
    centrosCustoDropdown: centrosCustoDropdown ?? [],
    funcionariosAprovacaoDropdown: funcionariosAprovacaoDropdown ?? [],
    aprovadorModal,
    aprovadorEmEdicao,
    handleAprovadorEdit,
    funcionarioAprovadorDeleteModal,
    filters,
    handleReload,
    handleAprovadorModalOpenState,
    handleAprovadorSubmit,
    handleFuncionarioAprovadorDeleteOpenState,
    handleFuncionarioAprovadorDeleteSubmit,
    onNomeEmailFuncionarioChange,
    handleCentroCustoChange,
    handleClearFilters,
  };
}
